import { Link } from 'react-router-dom';
import { Video, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { config } from '@/config/env';

const perks = [
  'HD video & audio for up to 1000 people',
  'Screen share, whiteboard & recording',
  'Live chat, reactions & file sharing',
  'Enterprise security with MFA',
];

export default function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="grid min-h-full lg:grid-cols-2">
      {/* ── Left: gradient brand panel (desktop) ── */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-600 via-indigo-600 to-fuchsia-600 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

        <Link to="/" className="relative flex items-center gap-2 text-2xl font-extrabold">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
            <Video size={20} />
          </span>
          {config.appName}
        </Link>

        <div className="relative">
          <h2 className="text-4xl font-black leading-tight">
            Meetings that <br /> just work.
          </h2>
          <p className="mt-4 max-w-sm text-indigo-100">
            Join thousands of teams running HD video meetings, right from the browser.
          </p>
          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 text-indigo-50">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                  <Check size={14} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative rounded-2xl bg-white/10 p-5 backdrop-blur">
          <p className="text-sm text-indigo-50">
            “ConnectMeet replaced three tools for us. Setup took minutes and the video quality is fantastic.”
          </p>
          <p className="mt-2 text-sm font-semibold">— A happy customer</p>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="relative flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white px-6 py-12 dark:from-slate-950 dark:to-slate-900">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center justify-center gap-2 text-xl font-extrabold lg:hidden">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white">
              <Video size={18} />
            </span>
            {config.appName}
          </Link>

          <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-8 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
            <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
